import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Sentence } from '@/types/article';
import { useAuth } from '@/hooks/useClerkAuth';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  ChevronLeft,
  ChevronRight,
  X,
  BookOpen,
  ExternalLink,
  Crown,
  Lock,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  canReadSentence,
  consumeSentence,
  getRemainingTrialSentences,
  TRIAL_SENTENCES_LIMIT,
} from '@/services/trialTracker';

interface SentenceReaderProps {
  sentences: Sentence[];
  onClose: () => void;
  originalLink?: string;
  title?: string;
  requireAuth?: boolean;
}

const SentenceReader = ({
  sentences,
  onClose,
  originalLink,
  title,
  requireAuth = false,
}: SentenceReaderProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showEnglish, setShowEnglish] = useState(true);
  const [showVietnamese, setShowVietnamese] = useState(true);
  const [showVerbs, setShowVerbs] = useState(true);
  const [showTrialPrompt, setShowTrialPrompt] = useState(false);
  const [remainingTrialSentences, setRemainingTrialSentences] = useState(
    getRemainingTrialSentences()
  );

  const currentSentence = sentences[currentIndex];

  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex]);

  const goToNext = useCallback(() => {
    if (currentIndex < sentences.length - 1) {
      // Check if user can proceed to next sentence
      if (!user && !requireAuth) {
        if (!canReadSentence()) {
          setShowTrialPrompt(true);
          return;
        }
        // Consume a sentence for trial users
        const updatedUsage = consumeSentence();
        setRemainingTrialSentences(
          TRIAL_SENTENCES_LIMIT - updatedUsage.sentencesRead
        );
      }
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentIndex, sentences.length, user, requireAuth]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentIndex, onClose, goToNext, goToPrevious]);

  const renderVerbsSection = () => {
    const verbCount = currentSentence.verbs!.length;

    return (
      <Card className="bg-gradient-card shadow-card border-learning-muted/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-learning-primary" />
              Wörter-Buch
            </div>
            <Badge variant="outline" className="text-xs">
              {verbCount} {verbCount === 1 ? 'word' : 'words'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {currentSentence.verbs!.map((verb, index) => (
            <div
              key={index}
              className="group p-4 bg-white/50 rounded-lg border border-learning-muted/40 hover:border-learning-primary/40 transition-colors duration-200 space-y-3"
            >
              <div className="font-semibold text-foreground text-lg group-hover:text-learning-primary transition-colors">
                {verb.german}
              </div>

              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <Badge
                    variant="secondary"
                    className="bg-learning-secondary/20 text-learning-secondary text-xs mt-0.5 flex-shrink-0"
                  >
                    🇬🇧 EN
                  </Badge>
                  <span className="text-sm text-muted-foreground leading-relaxed">
                    {verb.english}
                  </span>
                </div>

                <div className="flex items-start gap-3">
                  <Badge
                    variant="secondary"
                    className="bg-learning-accent/20 text-learning-accent text-xs mt-0.5 flex-shrink-0"
                  >
                    🇻🇳 VI
                  </Badge>
                  <span className="text-sm text-muted-foreground leading-relaxed">
                    {verb.vietnamese}
                  </span>
                </div>
              </div>
            </div>
          ))}

          {/* Helpful tip */}
          {/* <div className="text-xs text-muted-foreground text-center pt-2 border-t border-learning-muted/20">
            💡 These are challenging words identified for beginners
          </div> */}
        </CardContent>
      </Card>
    );
  };

  // Trial prompt modal
  if (showTrialPrompt) {
    return (
      <div className="fixed inset-0 bg-background z-50 flex items-center justify-center p-6">
        <Card className="max-w-md mx-auto">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-learning-primary" />
              {t('trialLimit.trialComplete')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              {t('trialLimit.sentencesLimitReached', {
                limit: TRIAL_SENTENCES_LIMIT,
              })}
            </p>
            <Alert>
              <Lock className="h-4 w-4" />
              <AlertDescription>
                {t('trialLimit.signUpToContinue')}
              </AlertDescription>
            </Alert>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => navigate('/sign-up')}
                className="bg-learning-primary hover:bg-learning-primary/90"
              >
                <Crown className="h-4 w-4 mr-2" />
                {t('auth.signUpFree')}
              </Button>
              <Button variant="outline" onClick={() => navigate('/sign-in')}>
                {t('auth.signIn')}
              </Button>
              <Button variant="ghost" onClick={onClose} className="mt-2">
                {t('sentenceReader.close')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-background z-50 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-hero p-4 text-primary-foreground">
        <div className="container max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-primary-foreground hover:bg-white/20"
            >
              <X className="h-4 w-4 mr-2" />
              {t('sentenceReader.close')}
            </Button>
            <div className="flex items-center gap-4">
              <div className="text-sm opacity-90">
                {t('sentenceReader.sentence')} {currentIndex + 1}{' '}
                {t('sentenceReader.of')} {sentences.length}
              </div>
              {/* {!user && remainingTrialSentences > 0 && (
                <Badge
                  variant="secondary"
                  className="bg-white/20 text-white text-xs"
                >
                  {t('trialLimit.trialRemaining', {
                    count: remainingTrialSentences,
                  })}
                </Badge>
              )} */}
            </div>
          </div>

          {/* Language toggles */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="english"
                checked={showEnglish}
                onCheckedChange={checked => setShowEnglish(checked === true)}
                className="border-white data-[state=checked]:bg-white data-[state=checked]:text-primary"
              />
              <label htmlFor="english" className="text-sm font-medium">
                {t('sentenceReader.english')}
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="vietnamese"
                checked={showVietnamese}
                onCheckedChange={checked => setShowVietnamese(checked === true)}
                className="border-white data-[state=checked]:bg-white data-[state=checked]:text-primary"
              />
              <label htmlFor="vietnamese" className="text-sm font-medium">
                {t('sentenceReader.vietnamese')}
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="verbs"
                checked={showVerbs}
                onCheckedChange={checked => setShowVerbs(checked === true)}
                className="border-white data-[state=checked]:bg-white data-[state=checked]:text-primary"
              />
              <label htmlFor="verbs" className="text-sm font-medium">
                Wörter-Buch
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        <div className="container max-w-7xl mx-auto p-6">
          {/* Main Layout - Responsive Grid */}
          <div
            className={cn(
              'grid grid-cols-1 gap-8 min-h-full',
              showVerbs ? 'xl:grid-cols-3' : 'xl:grid-cols-1'
            )}
          >
            {/* Sentences Column - Full width when no verbs, 2/3 when verbs shown */}
            <div
              className={cn(
                'space-y-8 animate-fade-in',
                showVerbs ? 'xl:col-span-2' : 'xl:col-span-1'
              )}
            >
              {/* German (always shown) */}
              <div className="text-center">
                <div className="text-sm font-medium text-learning-primary mb-3">
                  {t('sentenceReader.german')}
                </div>
                <p className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground leading-relaxed">
                  {currentSentence.german}
                </p>
              </div>

              {/* English */}
              {showEnglish && (
                <div className="text-center animate-slide-in">
                  <div className="text-sm font-medium text-learning-secondary mb-3">
                    {t('sentenceReader.english')}
                  </div>
                  <p className="text-lg md:text-xl lg:text-2xl text-muted-foreground leading-relaxed">
                    {currentSentence.english}
                  </p>
                </div>
              )}

              {/* Vietnamese */}
              {showVietnamese && (
                <div className="text-center animate-slide-in">
                  <div className="text-sm font-medium text-learning-accent mb-3">
                    {t('sentenceReader.vietnamese')}
                  </div>
                  <p className="text-lg md:text-xl lg:text-2xl text-muted-foreground leading-relaxed">
                    {currentSentence.vietnamese}
                  </p>
                </div>
              )}

              {/* Verbs section for mobile/tablet - shown below sentences */}
              {showVerbs && (
                <div className="xl:hidden mt-8">{renderVerbsSection()}</div>
              )}
            </div>

            {/* Verbs Sidebar - Only show when verbs exist and are enabled */}
            {showVerbs && (
              <div className="hidden xl:block">
                <div className="sticky top-6">{renderVerbsSection()}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-muted p-4">
        <div className="container max-w-4xl mx-auto flex items-center justify-between">
          <Button
            variant="outline"
            onClick={goToPrevious}
            disabled={currentIndex === 0}
            className="bg-white/50 backdrop-blur-sm"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            {t('sentenceReader.previous')}
          </Button>

          {/* Progress bar */}
          <div className="flex-1 mx-8">
            <div className="bg-learning-muted rounded-full h-2">
              <div
                className="bg-gradient-hero h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${((currentIndex + 1) / sentences.length) * 100}%`,
                }}
              />
            </div>
          </div>

          {currentIndex === sentences.length - 1 && originalLink ? (
            <Button
              variant="outline"
              onClick={() =>
                window.open(originalLink, '_blank', 'noopener,noreferrer')
              }
              className="bg-gradient-hero text-primary-foreground hover:opacity-90"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View Original Source
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={goToNext}
              disabled={currentIndex === sentences.length - 1}
              className="bg-white/50 backdrop-blur-sm"
            >
              {t('sentenceReader.next')}
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SentenceReader;
